import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Radio, ArrowLeft, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LiveCourtsStrip } from "@/components/LiveCourtsStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Side({ side, score, winner, serving, testid }) {
  return (
    <div className={`flex-1 min-w-0 border p-4 sm:p-5 transition-colors relative ${winner ? "border-[#A3E635] bg-[#A3E635]/10" : serving ? "serve-box" : "border-zinc-800 bg-zinc-950"}`} data-testid={testid} data-serving={serving ? "true" : undefined}>
      {serving && !winner && <span className="absolute -top-2 right-2 bg-black text-red-400 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 leading-none" data-testid={`${testid}-serve-tag`}>Serve</span>}
      <div className="text-xs sm:text-sm font-display font-black uppercase leading-tight truncate text-white" data-testid={`${testid}-name`}>{side?.name || "TBD"}</div>
      <div className={`font-mono font-black tabular-nums leading-none mt-2 text-6xl sm:text-8xl ${winner ? "text-[#A3E635]" : "text-white"}`} data-testid={`${testid}-score`}>{score ?? 0}</div>
      {winner && <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[#A3E635] font-black">Winner</div>}
    </div>
  );
}

export function LiveScoreboard({ main, compact = false }) {
  if (!main) {
    return (
      <div className="border border-zinc-800 bg-zinc-950 p-8 text-center text-zinc-500" data-testid="scoreboard-empty">
        <Trophy className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
        <div className="font-display uppercase tracking-widest">No Main Court match set</div>
        <div className="text-xs mt-1">The scoreboard lights up as soon as the TD puts a match on the Main Court.</div>
      </div>
    );
  }
  const status = main.status;
  return (
    <div className="border border-red-500/40 bg-black p-3 sm:p-4" data-testid="live-scoreboard">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 truncate" data-testid="scoreboard-tournament">{main.tournament}{main.court ? ` · Court ${main.court}` : ""}</div>
          <div className="text-xs sm:text-sm font-bold text-white truncate" data-testid="scoreboard-label">{main.label}{main.round ? ` · ${main.round}` : ""}</div>
        </div>
        <span data-testid="scoreboard-status"
          className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-2 py-1 border ${status === "live" ? "border-red-500 text-red-400" : status === "final" ? "border-[#A3E635] text-[#A3E635]" : "border-zinc-700 text-zinc-400"}`}>
          {status === "live" && <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />}
          {status === "live" ? `Live · Game ${main.game}` : status === "final" ? "Final" : "Up Next"}
        </span>
      </div>
      <div className="flex items-stretch gap-2 sm:gap-3">
        <Side side={main.side_a} score={main.score_a} winner={main.winner === "a"} serving={main.status !== "final" && main.first_serve === "a"} testid="scoreboard-a" />
        <div className="self-center text-zinc-600 font-mono text-xs sm:text-base">VS</div>
        <Side side={main.side_b} score={main.score_b} winner={main.winner === "b"} serving={main.status !== "final" && main.first_serve === "b"} testid="scoreboard-b" />
      </div>
      {main.games?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="scoreboard-games">
          {main.games.map((g, i) => (
            <span key={i} className="font-mono text-xs border border-zinc-800 px-2 py-1 text-zinc-300">G{i + 1} <b className="text-white">{g[0]}–{g[1]}</b></span>
          ))}
        </div>
      )}
      {!compact && <div className="mt-3 text-[10px] text-zinc-600 uppercase tracking-widest">Scores update live from the scorekeeper's phone · refreshed every 3s</div>}
    </div>
  );
}

export default function Live() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    const load = () => axios.get(`${API}/public/live`).then(({ data }) => { setData(data); setErr(false); }).catch(() => setErr(true));
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, []);
  const parent = window.location.hostname;
  const channel = data?.twitch_channel;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="live-page">
      <header className="px-4 sm:px-6 py-3 border-b border-zinc-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={user ? "/profile" : "/"} className="text-zinc-500 hover:text-white" data-testid="live-back"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-display font-black uppercase text-xl sm:text-2xl leading-none">
              <span className="relative flex h-3 w-3"><span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" /><span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" /></span>
              Bullpen Live
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 truncate">{data?.title || "Main Court broadcast & live scoreboard"}</div>
          </div>
        </div>
        <Link to="/court-board" data-testid="live-court-board-link" className="hidden sm:inline-flex items-center gap-1.5 border border-[#22D3EE]/50 text-[#22D3EE] hover:bg-[#22D3EE] hover:text-black px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-colors"><Radio className="w-3.5 h-3.5" /> Court Boards</Link>
      </header>
      <main className="p-3 sm:p-6 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <div className="space-y-4 min-w-0">
        <div className="aspect-video w-full bg-zinc-950 border border-zinc-800 relative" data-testid="twitch-player-wrap">
          {channel ? (
            <iframe title="Twitch stream" data-testid="twitch-player" src={`https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${parent}&muted=true`}
              allowFullScreen allow="autoplay; fullscreen" className="absolute inset-0 w-full h-full" />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500 p-6 text-center" data-testid="twitch-offline">
              <Radio className="w-10 h-10 mb-3 text-zinc-700" />
              <div className="font-display uppercase tracking-widest text-lg">Stream offline</div>
              <div className="text-xs mt-1">{err ? "Couldn't reach the live feed." : "The broadcast starts when the admin sets a Twitch channel."}</div>
            </div>
          )}
        </div>
        <LiveCourtsStrip courts={data?.courts} />
        </div>
        <div className="space-y-4">
          <LiveScoreboard main={data?.main} />
          {channel && (
            <a href={`https://twitch.tv/${channel}`} target="_blank" rel="noopener noreferrer" data-testid="twitch-chat-link"
              className="block text-center border border-purple-500/50 text-purple-300 hover:bg-purple-500 hover:text-white px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-colors">Open chat on Twitch · {channel}</a>
          )}
        </div>
      </main>
    </div>
  );
}
